import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Grid, Card, CircularProgress,
  TextField, MenuItem, Button, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, TablePagination, Stack, ToggleButton,
  ToggleButtonGroup, LinearProgress, Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import TokenIcon from "@mui/icons-material/Token";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TimerIcon from "@mui/icons-material/Timer";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import { getData, API_ENDPOINTS } from "../../services/apiService";
import BookingLogDetailDialog from "./BookingLogDetailDialog";
import KpiTile from "./_KpiTile";
import StatusChip from "./_StatusChip";
import { AI_OPS_BG } from "./_aiOpsTheme";

/**
 * Booking Logs tab of the AI Ops page (Module G). Shape mirrors
 * LogsTab.js so admins have one consistent navigation pattern.
 */

const STATUSES = ["extracted", "clarified", "confirmed", "abandoned", "error"];

function defaultRange() {
  const d = new Date();
  const to = d.toISOString().slice(0, 10);
  d.setDate(d.getDate() - 30);
  const from = d.toISOString().slice(0, 10);
  return { from, to };
}

const tokensFmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const moneyFmt = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);
const pctFmt = (n) => (n == null ? "—" : `${(Number(n) * 100).toFixed(1)}%`);
const msFmt = (n) => (n == null ? "—" : `${Math.round(Number(n))} ms`);

export default function BookingLogsTab() {
  const [range, setRange] = useState(defaultRange());
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [filters, setFilters] = useState({
    status: "",
    userId: "",
    minConfidence: "",
    maxConfidence: "",
    audioOnly: false,
  });
  const [quickFilter, setQuickFilter] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState(null);

  const [selected, setSelected] = useState(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const res = await getData(API_ENDPOINTS.adminBookingStats, {
        from: range.from, to: range.to,
      });
      setStats(res?.result || null);
    } catch (err) {
      setStatsError(err?.response?.data?.message || err.message);
    } finally {
      setStatsLoading(false);
    }
  }, [range.from, range.to]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const params = {
        from: range.from, to: range.to,
        limit: rowsPerPage, offset: page * rowsPerPage,
      };
      if (filters.status) params.status = filters.status;
      if (filters.userId) params.userId = filters.userId;
      if (filters.minConfidence !== "") params.minConfidence = filters.minConfidence;
      if (filters.maxConfidence !== "") params.maxConfidence = filters.maxConfidence;
      if (filters.audioOnly) params.audioOnly = "true";
      const res = await getData(API_ENDPOINTS.adminBookingLogs, params);
      setRows(res?.result?.rows || []);
      setTotal(res?.result?.total || 0);
    } catch (err) {
      setLogsError(err?.response?.data?.message || err.message);
    } finally {
      setLogsLoading(false);
    }
  }, [range.from, range.to, page, rowsPerPage, filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); },  [fetchLogs]);

  const applyQuickFilter = (key) => {
    setQuickFilter(key === quickFilter ? "" : key);
    setPage(0);
    switch (key) {
      case "abandoned":
        setFilters((f) => ({ ...f, status: "abandoned", minConfidence: "", maxConfidence: "", audioOnly: false }));
        break;
      case "clarified":
        setFilters((f) => ({ ...f, status: "clarified", minConfidence: "", maxConfidence: "", audioOnly: false }));
        break;
      case "errors":
        setFilters((f) => ({ ...f, status: "error", minConfidence: "", maxConfidence: "", audioOnly: false }));
        break;
      case "audio":
        setFilters((f) => ({ ...f, status: "", minConfidence: "", maxConfidence: "", audioOnly: true }));
        break;
      default:
        setFilters({ status: "", userId: "", minConfidence: "", maxConfidence: "", audioOnly: false });
    }
  };

  const statusBarMaxPct = useMemo(() => {
    if (!stats?.byStatus?.length) return 0;
    return Math.max(...stats.byStatus.map((d) => d.pct));
  }, [stats]);

  const errorRateHigh = stats?.errorRate > 0.05;
  const clarifiedPct = stats?.byStatus?.find((s) => s.status === "clarified")?.pct;

  return (
    <Box>
      {/* Date range + refresh */}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
        <TextField
          label="From" type="date" size="small"
          value={range.from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To" type="date" size="small"
          value={range.to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchStats(); fetchLogs(); }}
          sx={{
            background: "linear-gradient(135deg, #22D3EE, #0891B2)",
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "0 4px 12px rgba(8,145,178,0.30)",
            "&:hover": { background: "linear-gradient(135deg, #0891B2, #0E7490)" },
          }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Stats banner */}
      {statsError ? (
        <Card elevation={0} sx={{ border: "1px solid #fca5a5", bgcolor: "#fee2e2", p: 2, mb: 2 }}>
          <Typography color="error">Stats failed: {statsError}</Typography>
        </Card>
      ) : statsLoading ? (
        <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>
      ) : stats ? (
        <>
          <Box sx={{ display:"flex", flexWrap:"wrap", gap:2, mb:3 }}>
            <KpiTile label="Total Calls"     value={stats.totalCalls?.toLocaleString()??"0"}        accent="cyan"    icon={ReceiptLongIcon} />
            <KpiTile label="Unique Users"    value={stats.uniqueUsers?.toLocaleString()??"0"}       accent="violet"  icon={PeopleAltIcon} />
            <KpiTile label="Total Tokens"    value={tokensFmt(stats.tokenUsage?.totalTokens)}       accent="indigo"  icon={TokenIcon} />
            <KpiTile label="Est. Cost"       value={moneyFmt(stats.tokenUsage?.estimatedCostUsd)}   accent="emerald" icon={AttachMoneyIcon} />
            <KpiTile label="Avg Latency"     value={msFmt(stats.avgLatencyMs)}                      accent="amber"   icon={TimerIcon} />
            <KpiTile label="Error Rate"      value={pctFmt(stats.errorRate)} accent={errorRateHigh?"rose":"slate"} icon={ErrorOutlineIcon} warning={errorRateHigh} />
            <KpiTile label="Audio Calls"     value={pctFmt(stats.audioCallsPct)}                    accent="pink"    icon={GraphicEqIcon} />
            <KpiTile label="Clarification %" value={clarifiedPct!=null?`${clarifiedPct}%`:"—"}     accent="amber"   icon={HelpOutlineIcon} />
          </Box>

          {/* Status distribution */}
          {stats.byStatus?.length > 0 && (
            <Card elevation={0} sx={{ border: `1px solid ${AI_OPS_BG.cardBorder}`, borderRadius: 2.5, p: 2.5, mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <StackedBarChartIcon sx={{ fontSize: 18, color: "#0891B2" }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.3 }}>
                  Status distribution
                </Typography>
              </Stack>
              <Stack spacing={1.25}>
                {stats.byStatus.map((d) => (
                  <Box key={d.status}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StatusChip status={d.status} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {d.count.toLocaleString()} <span style={{ opacity: 0.6 }}>({d.pct}%)</span>
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={statusBarMaxPct > 0 ? (d.pct / statusBarMaxPct) * 100 : 0}
                      sx={{
                        height: 6, borderRadius: 3, bgcolor: "#ECFEFF",
                        "& .MuiLinearProgress-bar": {
                          background: "linear-gradient(90deg, #67E8F9, #06B6D4)",
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Card>
          )}

          {/* Top missing fields */}
          {stats.topMissingFields?.length > 0 && (
            <Card elevation={0} sx={{ border: `1px solid ${AI_OPS_BG.cardBorder}`, borderRadius: 2.5, p: 2.5, mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <ReportProblemIcon sx={{ fontSize: 18, color: "#EA580C" }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.3 }}>
                  Top missing fields — where users keep tripping up
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {stats.topMissingFields.map((m) => (
                  <Chip
                    key={m.field}
                    size="small"
                    label={`${m.field} · ${m.count}`}
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      bgcolor: "#FFEDD5",
                      color: "#9A3412",
                      height: 24,
                    }}
                  />
                ))}
              </Stack>
            </Card>
          )}
        </>
      ) : null}

      {/* Quick filters */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>
          Quick filters
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={quickFilter}
          onChange={(_, v) => applyQuickFilter(v || "")}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12,
              px: 1.5,
              borderColor: "#CBD5E1",
            },
            "& .Mui-selected": {
              background: "linear-gradient(135deg, #CFFAFE, #A5F3FC) !important",
              color: "#0E7490 !important",
              borderColor: "#67E8F9 !important",
            },
          }}
        >
          <ToggleButton value="abandoned">
            <ExitToAppIcon sx={{ fontSize: 14, mr: 0.5 }} /> Abandoned
          </ToggleButton>
          <ToggleButton value="clarified">
            <ChatRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} /> Clarified
          </ToggleButton>
          <ToggleButton value="errors">
            <ErrorOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} /> Errors
          </ToggleButton>
          <ToggleButton value="audio">
            <GraphicEqRoundedIcon sx={{ fontSize: 14, mr: 0.5 }} /> Audio only
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Filter bar */}
      <Card elevation={0} sx={{ border: `1px solid ${AI_OPS_BG.cardBorder}`, borderRadius: 2.5, p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" select label="Status"
              value={filters.status}
              onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(0); }}>
              <MenuItem value="">(any)</MenuItem>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="User ID" type="number"
              value={filters.userId}
              onChange={(e) => { setFilters({ ...filters, userId: e.target.value }); setPage(0); }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Min Confidence" type="number"
              inputProps={{ step: 0.05, min: 0, max: 1 }}
              value={filters.minConfidence}
              onChange={(e) => { setFilters({ ...filters, minConfidence: e.target.value }); setPage(0); }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Max Confidence" type="number"
              inputProps={{ step: 0.05, min: 0, max: 1 }}
              value={filters.maxConfidence}
              onChange={(e) => { setFilters({ ...filters, maxConfidence: e.target.value }); setPage(0); }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <Button fullWidth size="small" variant="outlined"
              sx={{ textTransform: "none", fontWeight: 700 }}
              onClick={() => { setFilters({ status: "", userId: "", minConfidence: "", maxConfidence: "", audioOnly: false }); setQuickFilter(""); setPage(0); }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Logs table */}
      <Card elevation={0} sx={{ border: `1px solid ${AI_OPS_BG.cardBorder}`, borderRadius: 2.5, overflow: "hidden" }}>
        {logsLoading ? (
          <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
        ) : logsError ? (
          <Box sx={{ p: 2 }}><Typography color="error">Logs failed: {logsError}</Typography></Box>
        ) : (
          <>
            <Box sx={{ maxHeight: 620, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        bgcolor: "#F8FAFC",
                        fontWeight: 800,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "#475569",
                        borderBottom: "1px solid #E2E8F0",
                      },
                    }}
                  >
                    <TableCell>Time</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Request</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Conf.</TableCell>
                    <TableCell align="right">Latency</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell align="center">🎤</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                          No rows in this range
                        </Typography>
                        <Typography color="text.disabled" variant="caption">
                          Try widening the date filter or clearing quick filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:nth-of-type(odd)": { bgcolor: AI_OPS_BG.zebra },
                        "&:hover": { bgcolor: `${AI_OPS_BG.rowHover} !important` },
                      }}
                      onClick={() => setSelected(r)}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 11, color: "#64748B" }}>
                        {new Date(r.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.userId ?? "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Tooltip title={r.requestText || ""}>
                          <Typography noWrap variant="body2" sx={{ fontSize: 13 }}>{r.requestText}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell><StatusChip status={r.status} /></TableCell>
                      <TableCell>
                        {r.confidence != null ? (
                          <Chip
                            size="small"
                            label={r.confidence.toFixed(2)}
                            color={r.confidence < 0.6 ? "warning" : "default"}
                            variant="outlined"
                            sx={{ fontSize: 10, height: 22 }}
                          />
                        ) : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                        {msFmt(r.latencyMs)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                        {tokensFmt(r.tokens?.total)}
                      </TableCell>
                      <TableCell align="center">{r.isAudio ? "🎤" : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={total} page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{ borderTop: "1px solid #E5E7EB" }}
            />
          </>
        )}
      </Card>

      <BookingLogDetailDialog row={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}
